import { ref } from 'vue'
import { defineStore } from 'pinia'
import { http } from '@/helpers/http'
import { useLoaderStore } from '@/stores/loader'
import { useSnackbarStore } from '@/stores/snackbar'
import { usePopupStore } from '@/stores/popup'
import type { TAiVoice } from '@/types/voice'

export const useAiTtsStore = defineStore('ai-tts', () => {
  const loader = useLoaderStore()
  const snack = useSnackbarStore()
  const popup = usePopupStore()

  const voices = ref<TAiVoice[]>([])
  const selectedVoice = ref<TAiVoice | null>(null)
  const audioUrl = ref<string | null>(null)
  const lastGenerateCached = ref<boolean | null>(null)

  async function fetchVoices() {
    try {
      const res = await http.get<{ status: string; result: TAiVoice[] }>('/ai-tts/voices')
      if (res?.status === 'success') {
        voices.value = res.result
      }
    } catch (err) {
      console.error('Error fetching voices:', err)
      snack.add('Failed to load voices', { type: 'danger' })
    }
  }

  function uniqueVoiceTitle(base: string): string {
    const existing = voices.value.map((v) => v.title)
    if (!existing.includes(base)) return base

    let n = 2
    while (existing.includes(`${base} ${n}`)) n++

    return `${base} ${n}`
  }

  async function createVoice(payload: { name: string; description: string; audio: File; language: string }) {
    loader.show('Uploading and training...')

    const formData = new FormData()
    formData.append('name', payload.name)
    formData.append('description', payload.description)
    formData.append('audio', payload.audio)
    formData.append('language', payload.language)

    try {
      const response = await fetch('/ai-tts/create-voice', {
        method: 'POST',
        body: formData,
      })
      const data = await response.json()

      if (data.status === 'success') {
        snack.add('Voice created successfully!', { type: 'success' })
        await fetchVoices()
        return true
      } else {
        snack.add(data.message || 'Error creating voice', { type: 'danger' })
        return false
      }
    } catch (err) {
      console.error('Create voice error:', err)
      snack.add('Upload failed', { type: 'danger' })
      return false
    } finally {
      loader.hide()
    }
  }

  async function deleteVoice(id: string) {
    const result = await popup.confirm('Are you sure you want to delete this voice?', {
      content: 'This action is irreversible.',
      labelConfirm: 'Delete',
    })

    if (!result) return

    loader.show('Deleting...')
    try {
      await http.post(`/ai-tts/delete-voice`, { id })
      snack.add('Voice deleted', { type: 'success' })

      if (selectedVoice.value?._id === id) {
        selectedVoice.value = null
      }

      await fetchVoices()
    } catch (err) {
      console.error('Delete error:', err)
      snack.add('Delete failed', { type: 'danger' })
    } finally {
      loader.hide()
    }
  }

  async function renameVoice(voice: TAiVoice): Promise<boolean> {
    const input = await popup.prompt('Rename voice', { initial: voice.title, labelConfirm: 'Rename' })
    const title = input ? input.trim() : ''

    if (!title || title === voice.title) return false

    const uniqueTitle = uniqueVoiceTitle(title)

    try {
      await http.post(`/ai-tts/update-voice`, { id: voice._id, title: uniqueTitle })
      voice.title = uniqueTitle
      snack.add('Voice renamed', { type: 'success' })

      return true
    } catch (err) {
      console.error('Update error:', err)
      snack.add('Failed to rename the voice', { type: 'danger' })

      return false
    }
  }

  async function generateSpeech(text: string, voiceId?: string, isPreview: boolean = false) {
    const modelId = voiceId || selectedVoice.value?._id

    if (!modelId || !text) return

    loader.show('Generating speech...')
    try {
      const response = await http.postRaw('/ai-tts/generate-speech', {
        model_id: modelId,
        text,
        isPreview,
      })

      if (!response.ok) throw new Error('Generation failed')

      lastGenerateCached.value = response.headers.get('X-TTS-Cached') === 'true'

      const blob = await response.blob()

      if (audioUrl.value) {
        URL.revokeObjectURL(audioUrl.value)
      }

      audioUrl.value = URL.createObjectURL(blob)
      snack.add('Speech generated!', { type: 'success' })
    } catch (err) {
      console.error('TTS Generation error:', err)
      lastGenerateCached.value = null
      snack.add('Failed to generate speech', { type: 'danger' })
    } finally {
      loader.hide()
    }
  }

  const narrationCache = new Map<string, string>()

  function narrationKey(text: string, voiceId: string, speed?: number): string {
    return `${voiceId}|${speed ?? 1}|${text}`
  }

  function cachedNarration(text: string, voiceId: string, speed?: number): string | null {
    return narrationCache.get(narrationKey(text, voiceId, speed)) ?? null
  }

  async function ensureNarration(text: string, voiceId: string, speed?: number): Promise<string | null> {
    if (!text || !voiceId) return null

    const key = narrationKey(text, voiceId, speed)
    const existing = narrationCache.get(key)

    if (existing) return existing

    try {
      const response = await http.postRaw('/ai-tts/generate-speech', {
        model_id: voiceId,
        text,
        speed: speed ?? null,
        isPreview: true,
      })

      if (!response.ok) return null

      const blob = await response.blob()
      const url = URL.createObjectURL(blob)

      narrationCache.set(key, url)

      return url
    } catch {
      return null
    }
  }

  return {
    voices,
    selectedVoice,
    audioUrl,
    lastGenerateCached,
    fetchVoices,
    uniqueVoiceTitle,
    createVoice,
    deleteVoice,
    renameVoice,
    generateSpeech,
    cachedNarration,
    ensureNarration,
  }
})
